import AgentDefault from '@/components/layout/AgentDefault';
import Default from '@/components/layout/Default';
import { normalizeNationalId } from '@/utils/paValidation';

export const PA_JOURNEY_DIRECT = 'direct';
export const PA_ORIGIN_CUSTOMER = 'CUSTOMER_PORTAL';

/** Customer PA product card — on unless explicitly disabled. APIs follow VUE_APP_MOCK_AGENT_PA. */
export function isCustomerPaEnabled() {
  const disabled = process.env.VUE_APP_CUSTOMER_PA_DISABLED;
  return disabled !== 'true' && disabled !== '1';
}

export function resolvePaChannel(routeMeta) {
  return routeMeta?.paChannel === 'customer' ? 'customer' : 'agent';
}

export function getPaChannelConfig(routeMeta) {
  const channel = resolvePaChannel(routeMeta);
  if (channel === 'customer') {
    return {
      channel: 'customer',
      layout: Default,
      defaultJourney: PA_JOURNEY_DIRECT,
      origin: PA_ORIGIN_CUSTOMER,
      routes: {
        products: 'CustomerProductSelect',
        planSelect: 'CustomerPaPlanSelect',
        sale: 'CustomerPaSale',
        finalization: 'CustomerPaFinalization',
        dashboard: 'Dashboard',
      },
      labels: {
        planBreadcrumb: 'Buy cover / Personal Accident',
        planTitle: 'Students PA — Select cover',
        saleBreadcrumb: 'Personal Accident / Application',
        saleTitle: 'Complete application',
        idSection: 'Cover & your details',
        idHint: '',
        customerIdHint: 'We confirm your identity from your Pacis account.',
        continueSidebar: 'Continue',
        newSaleSub: 'This will clear your current application progress.',
      },
      hideVerifiedName: true,
      trackAnalytics: false,
      saleSteps: ['Your details', 'Health', 'Next of kin', 'Payment'],
    };
  }
  return {
    channel: 'agent',
    layout: AgentDefault,
    defaultJourney: 'quote',
    origin: null,
    routes: {
      products: 'AgentProductSelect',
      planSelect: 'AgentPaPlanSelect',
      sale: 'AgentPaSale',
      finalization: 'AgentPaFinalization',
      dashboard: 'AgentDashboard',
    },
    labels: {
      planBreadcrumb: 'New Quote / Personal Accident',
      planTitle: 'Students PA — Select cover',
      saleBreadcrumb: 'Personal Accident / Complete application',
      saleTitle: 'Complete sale',
      idSection: 'Cover & customer',
      idHint: '',
      customerIdHint: 'Enter first name and ID number to verify the customer.',
      continueSidebar: null,
      newSaleSub: 'This will clear your current PA progress.',
    },
    hideVerifiedName: false,
    trackAnalytics: true,
    saleSteps: ['Customer', 'Health', 'Next of kin', 'Payment'],
  };
}

/**
 * Customer Cognito attributes from signup (custom:id_number, custom:kra-pin, name, etc.).
 */
export function getCognitoIdentityProfile(user) {
  if (!user) return null;
  const idNumber = normalizeNationalId(user['custom:id_number']);
  const fullName = String(user.name || '').trim();
  const firstName = fullName.split(/\s+/).filter(Boolean)[0] || '';
  const kraRaw = user['custom:kra-pin'] || user.preferred_username || '';
  const kraPin = String(kraRaw).replace(/\s/g, '').toUpperCase().slice(0, 11);
  let phone = String(user.phone_number || '').replace(/\D/g, '');
  if (phone.startsWith('254') && phone.length === 12) phone = `0${phone.slice(3)}`;
  if (phone.startsWith('254') && phone.length > 12) phone = phone.slice(3);
  if (phone.length === 9 && (phone.startsWith('7') || phone.startsWith('1'))) phone = `0${phone}`;
  return {
    idNumber,
    fullName,
    firstName: firstName.toUpperCase(),
    kraPin,
    email: String(user.email || '').trim(),
    phone,
  };
}

export function buildPaCreatePayload(config, {
  optionCode, courseDuration, premium, journey, agentId, agnShtDesc,
}) {
  const resolvedJourney = config.channel === 'customer'
    ? config.defaultJourney
    : (journey || config.defaultJourney);
  const payload = {
    optionCode,
    courseDuration,
    premium,
    journey: resolvedJourney,
  };
  if (config.origin) {
    payload.origin = config.origin;
  }
  const resolvedAgnShtDesc = String(agnShtDesc ?? agentId ?? '').trim();
  if (resolvedAgnShtDesc && resolvedAgnShtDesc !== '0' && resolvedAgnShtDesc.toUpperCase() !== 'DIRECT') {
    payload.agnShtDesc = resolvedAgnShtDesc;
    payload.agentId = resolvedAgnShtDesc;
  } else if (config.channel === 'customer') {
    payload.agnShtDesc = 'DIRECT';
  }
  return payload;
}

/** GIS short description for PA quotation attribution (not numeric agency code). */
export function referralPaAgentId(agent) {
  const shortDesc = String(agent?.accountNo || '').trim();
  if (shortDesc) return shortDesc;
  const code = String(agent?.referralCode || agent?.agencyCode || '').trim();
  if (!code || code === '0' || code.toUpperCase() === 'DIRECT') return null;
  return code;
}
